import React, { useState, useEffect, useRef, useCallback } from 'react';
import { validateBetaKey } from '../../DatabaseAPI';
import {
    getStoredInviteKey,
    hasInviteLinkError,
    clearStoredInvite,
    setStoredInviteKey
} from '../../auth/inviteKey';

function BetaKeyValidation({ onValidated, onBack }) {
    // Read the pending invite ONCE, as lazy initial state, so the field behaves
    // like a normal editable input from the first render. An effect that wrote
    // the value in after mount would fight the user if they started typing, and
    // would re-apply the invite every time this screen re-rendered.
    const [betaKey, setBetaKey] = useState(() => getStoredInviteKey() || '');

    // Frozen at mount for the same reason: this drives explanatory copy about how
    // the user GOT here, which must not change under them as they edit the field.
    //
    // `arrivedViaInviteLink` means "this screen was reached by clicking an invite
    // link", which INCLUDES a link that was rejected and therefore stored no key.
    // That case still needs invite-flow treatment — the explanatory banner, and
    // persistence of whatever key the user types to replace the bad one.
    const [inviteLinkWasMalformed] = useState(() => hasInviteLinkError());
    const [arrivedViaInviteLink] = useState(
        () => Boolean(getStoredInviteKey()) || hasInviteLinkError()
    );

    // Narrower: a USABLE key was prefilled. Drives the auto-advance and the
    // "invite applied" banner, neither of which applies to a rejected link.
    const [arrivedWithInvite] = useState(() => Boolean(getStoredInviteKey()));

    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    /**
     * Validate `keyToValidate` and, on success, hand off to registration.
     *
     * Shared by the manual submit button and the invite auto-advance below, so
     * both paths get identical error handling — an invite that fails must land
     * the user on the SAME recoverable form a mistyped manual key would.
     *
     * `fromInviteLink` tightens the accept criteria for URL-borne keys only.
     * Manual entry is deliberately unchanged: someone typing their own `INDV_`
     * key into the form is the intended use of an individual key.
     */
    const runValidation = useCallback(async (keyToValidate, { fromInviteLink = false } = {}) => {
        setError('');
        setIsLoading(true);

        try {
            // The key itself is never logged. These are shared multi-use codes,
            // and console output ends up in screen shares, bug-report
            // screenshots, and session-replay tools.
            console.log('BetaKeyValidation: Submitting beta key for validation');
            const response = await validateBetaKey(keyToValidate);

            if (response.valid) {
                // AUTHORITATIVE SHARED-KEY GATE for URL invites.
                //
                // The `GRUP_` prefix filter in inviteKey.js is a convenience
                // check on a naming convention an admin can hand-edit. THIS is
                // the real boundary: `key_type` is the model field the backend
                // actually stores, returned by the validation response.
                //
                // It has to be enforced here because the backend will not do it
                // for us — `UserRegistrationSerializer.create()` re-runs
                // `is_valid()` (active/limit/expiry) but never calls
                // `can_be_used_by_email()`, so an INDIVIDUAL key's `target_email`
                // binding is NOT enforced at redemption. A leaked `INDV_` link
                // would otherwise let a stranger consume someone else's invite
                // under their own email.
                const keyType = response.betaKey?.key_type;
                if (fromInviteLink && keyType !== 'SHARED') {
                    console.error(
                        'BetaKeyValidation: Invite link carried a non-shared key ' +
                        `(key_type=${keyType}); refusing to auto-redeem it.`
                    );
                    // Drop the stored invite: it is not usable by this path, and
                    // leaving it would re-trigger the same rejection on refresh.
                    clearStoredInvite();
                    setBetaKey('');
                    setError(
                        'This invite link cannot be used. Invite links only work with ' +
                        'shared event keys. If you received a personal beta key, please ' +
                        'enter it below.'
                    );
                    return;
                }

                console.log('BetaKeyValidation: Beta key valid, advancing to registration');
                // Deliberately NOT cleared here. The key is not spent until
                // registration succeeds, and dropping it now would leave the user
                // with an empty field if they refresh or back out of the next step.
                onValidated(response.betaKey);
            } else {
                // 200-with-valid:false. Surface the backend's own wording.
                console.log('BetaKeyValidation: Beta key rejected by backend');
                setError(response.message || 'Invalid beta key');
            }
        } catch (error) {
            console.error('Beta key validation error:', error);

            // PRESERVE THE BACKEND'S REASON. The API answers with a specific,
            // user-actionable message — "This beta key has already been used",
            // "This beta key is not valid or has expired", "Invalid beta key" —
            // and `validateBetaKey()` already rethrows it as `error.message`
            // for non-2xx responses (a rejected key is a 400, so it lands HERE,
            // not in the branch above).
            //
            // Flattening all of that to "Failed to validate beta key" told an
            // exhausted-key user to "try again", which is advice that can never
            // work, and hid the one fact that would send them to ask for a new
            // key. Only a genuine transport failure gets the generic text.
            const isTransportFailure = (
                error instanceof TypeError ||          // fetch network error
                /^HTTP \d{3}:/.test(error.message)     // no JSON body to quote
            );

            setError(
                isTransportFailure || !error.message
                    ? 'Could not reach the server to check that key. Please try again.'
                    : error.message
            );
        } finally {
            setIsLoading(false);
        }
    }, [onValidated]);

    const handleSubmit = (e) => {
        e.preventDefault();

        // PERSIST A HAND-CORRECTED KEY before validating it.
        //
        // Someone who arrived on a bad invite link and fixed the key in the field
        // held that correction in React state only — storage still had the
        // original. A refresh mid-signup silently reverted the field to the
        // broken value and undid their work. Writing it back keeps the two in
        // agreement, so a reload resumes with the key they actually intend.
        //
        // Only for invite arrivals: a walk-up manual entry has no stored invite
        // and must not acquire one, or it would inherit invite-flow routing.
        const trimmedKey = betaKey.trim();
        if (arrivedViaInviteLink && trimmedKey && trimmedKey !== getStoredInviteKey()) {
            console.log('BetaKeyValidation: Persisting hand-corrected invite key');
            setStoredInviteKey(trimmedKey);
        }

        runValidation(trimmedKey);
    };

    /**
     * INVITE AUTO-ADVANCE — skip this screen when the key came from a link.
     *
     * The user already "pressed Go" by clicking the invite link, so making them
     * press it again on a field they cannot meaningfully review is a dead step.
     *
     * SAFE ONLY BECAUSE VALIDATION IS READ-ONLY: `BetaKeyValidationView` looks
     * the key up and returns its metadata — it does NOT call `use_key()` and does
     * NOT increment `usage_count`. The seat is spent at REGISTRATION, which is
     * still an explicit user action behind an email/password form. So a crawler
     * or link preview that loads the invite URL costs at most one lookup and can
     * never burn a seat. **If validation ever becomes stateful, this
     * auto-advance must be reverted** — that inverts the whole safety argument.
     *
     * Only fires for invite arrivals; a manually typed key still needs the button.
     */
    const autoAdvanceAttempted = useRef(false);

    useEffect(() => {
        if (!arrivedWithInvite) {
            return;
        }
        // React 18 StrictMode mounts effects twice in development, and this one
        // performs a network call — the ref makes it fire exactly once per
        // screen so a failed invite cannot retry itself into a loop.
        if (autoAdvanceAttempted.current) {
            return;
        }
        autoAdvanceAttempted.current = true;

        const invite = getStoredInviteKey();
        if (invite) {
            console.log('BetaKeyValidation: Auto-validating invite key from link');
            runValidation(invite, { fromInviteLink: true });
        }
    }, [arrivedWithInvite, runValidation]);

    return (
        <div style={{ 
            maxWidth: '400px', 
            margin: '0 auto', 
            padding: '40px 20px',
            fontFamily: 'Arial, sans-serif'
        }}>
            <div style={{
                backgroundColor: '#2c3e50',
                color: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
                <h2 style={{ 
                    textAlign: 'center', 
                    marginTop: '0',
                    marginBottom: '30px' 
                }}>
                    {arrivedWithInvite && !error ? '🎟️ Your Invite' : '🔐 Enter Beta Key'}
                </h2>

                {/* Reassurance while the key is still unjudged. Suppressed once an
                    error exists: "Invite key applied" directly above "this key is
                    invalid" reads as a contradiction, and the red error is the
                    message that matters. On success this screen unmounts. */}
                {arrivedWithInvite && !isLoading && !error && (
                    <div style={{
                        backgroundColor: 'var(--color-success)',
                        color: 'var(--color-text-on-primary)',
                        padding: 'var(--space-3)',
                        borderRadius: 'var(--radius-sm)',
                        marginBottom: 'var(--space-5)',
                        textAlign: 'center',
                        fontSize: 'var(--font-size-base)',
                        lineHeight: '1.4'
                    }}>
                        Invite key applied from your link.
                    </div>
                )}

                {arrivedWithInvite && isLoading && (
                    <div style={{
                        backgroundColor: 'var(--color-info)',
                        color: 'var(--color-text-on-primary)',
                        padding: 'var(--space-3)',
                        borderRadius: 'var(--radius-sm)',
                        marginBottom: 'var(--space-5)',
                        textAlign: 'center',
                        fontSize: 'var(--font-size-base)',
                        lineHeight: '1.4'
                    }}>
                        Checking your invite…
                    </div>
                )}

                {inviteLinkWasMalformed && (
                    <div style={{
                        backgroundColor: 'var(--color-warning)',
                        color: 'var(--color-text-on-primary)',
                        padding: 'var(--space-3)',
                        borderRadius: 'var(--radius-sm)',
                        marginBottom: 'var(--space-5)',
                        textAlign: 'center',
                        fontSize: 'var(--font-size-base)',
                        lineHeight: '1.4'
                    }}>
                        That invite link didn't look right, so we couldn't fill it in.
                        Please enter your key manually.
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '20px' }}>
                        <label htmlFor="betaKey" style={{ 
                            display: 'block', 
                            marginBottom: '8px',
                            fontWeight: 'bold'
                        }}>
                            Beta Key:
                        </label>
                        <input
                            type="text"
                            id="betaKey"
                            value={betaKey}
                            onChange={(e) => setBetaKey(e.target.value.toUpperCase())}
                            required
                            disabled={isLoading}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '4px',
                                border: '1px solid #bdc3c7',
                                fontSize: '16px',
                                fontFamily: 'monospace',
                                boxSizing: 'border-box'
                            }}
                            // Matches what generate_key_code() actually emits; the
                            // old BETA-XXXXXXXX-XXXX example was a format the
                            // system has never produced.
                            placeholder="GRUP_XXXXXXXX or INDV_XXXXXXXX"
                        />
                    </div>

                    {error && (
                        <div style={{
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            padding: '10px',
                            borderRadius: '4px',
                            marginBottom: '20px',
                            textAlign: 'center'
                        }}>
                            {error}
                        </div>
                    )}

                    <div style={{ textAlign: 'center' }}>
                        <button
                            type="submit"
                            disabled={isLoading || !betaKey}
                            style={{
                                backgroundColor: isLoading ? '#95a5a6' : '#27ae60',
                                color: 'white',
                                padding: '12px 24px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                marginRight: '10px'
                            }}
                        >
                            {/* No 'Go' variant any more: an invite that succeeds
                                never renders this button, so the only person
                                reading it is retrying after a failure — and 'Go'
                                would imply the original invite is still in play. */}
                            {isLoading ? 'Validating...' : 'Validate Key'}
                        </button>

                        <button
                            type="button"
                            onClick={onBack}
                            disabled={isLoading}
                            style={{
                                backgroundColor: '#95a5a6',
                                color: 'white',
                                padding: '12px 24px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                fontSize: '16px'
                            }}
                        >
                            ← Back
                        </button>
                    </div>
                </form>

                <div style={{ 
                    marginTop: '30px', 
                    padding: '15px',
                    backgroundColor: '#34495e',
                    borderRadius: '4px',
                    fontSize: '14px',
                    lineHeight: '1.4'
                }}>
                    <strong>ℹ️ Beta Access</strong><br/>
                    Enter your beta key to access Mythic Football. You'll provide your email on the next step.
                </div>
            </div>
        </div>
    );
}

export default BetaKeyValidation;
